import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { BaseResponse } from "../models/base.response";
import { User } from "../models/user.interface";
import { BookAppointmentDto } from '../models/coach.dto';
import { USER_TYPES } from "../shared/constants";

@Injectable({
    providedIn: 'root'
})
export class UsersService {

    constructor(private http: HttpClient) {
    }

    find(typeId: any, name: string): Observable<BaseResponse<User[]>> {
        var params = new HttpParams();
        if (typeId) {
            params = params.set('userTypeId', typeId);
        }
        if (name && name.trim().length > 0) {
            params = params.set('name', name);
        }
        return this.http.get<BaseResponse<User[]>>(`${environment.apiUrl}/users`, { params: params });
    }

    findCustomersByCoach(coachId: string, name: string): Observable<BaseResponse<User[]>> {
        var params = new HttpParams();
        params = params.set('userTypeId', USER_TYPES.Customer.id);
        params = params.set('coachId', coachId);
        if (name && name.trim().length > 0) {
            params = params.set('name', name);
        }
        return this.http.get<BaseResponse<User[]>>(`${environment.apiUrl}/users`, { params: params });
    }

    findById(id: string): Observable<BaseResponse<User>> {
        return this.http.get<BaseResponse<User>>(`${environment.apiUrl}/users/${id}`);
    }

    findAll(): Observable<BaseResponse<User[]>> {
        return this.http.get<BaseResponse<User[]>>(`${environment.apiUrl}/users`);
    }

    updateRegisterQuestions(id: string, questions: any): Observable<BaseResponse<any>> {
        const param = { id, questions }
        return this.http.patch<BaseResponse<any>>(`${environment.apiUrl}/users/${id}/register-questions`, param);
    }

    updateOnboardingComplete(id: string): Observable<BaseResponse<User>> {
        return this.http.patch<BaseResponse<User>>(`${environment.apiUrl}/users/${id}/onboarding-complete`, null);
    }

    editProfile(payload: { _id: string, [key: string]: any }): Observable<BaseResponse<User>> {
        return this.http.post<BaseResponse<User>>(`${environment.apiUrl}/users/complete-profile`, payload);
    }

    bookAppointment(mentorId: string, appointmentData: BookAppointmentDto, clientId: string): Observable<BaseResponse<User>> {
        return this.http.post<BaseResponse<User>>(`${environment.apiUrl}/users/${mentorId}/book-appointment`, appointmentData);
    }

    getAllUsersWithSubscriptions(): Observable<BaseResponse<any>> {
        return this.http.get<BaseResponse<any>>(`${environment.apiUrl}/users/admin/all-with-subscriptions`);
    }

    getUserWithSubscription(userId: string): Observable<BaseResponse<any>> {
        return this.http.get<BaseResponse<any>>(`${environment.apiUrl}/users/${userId}/with-subscription`);
    }

    assignRoles(userId: string, roles: string[]): Observable<BaseResponse<User>> {
        return this.http.put<BaseResponse<User>>(`${environment.apiUrl}/users/${userId}/roles`, { roles });
    }

    updateStatus(userId: string, statusId: number): Observable<BaseResponse<User>> {
        return this.http.put<BaseResponse<User>>(`${environment.apiUrl}/users/${userId}/status`, { statusId });
    }

    // ******* USUARIOS PAGINADOS ********
    getAllUsersWithSubscriptionsPaginated(page: number, pageSize: number): Observable<BaseResponse<any>> {
        return this.http.get<BaseResponse<any>>(`${environment.apiUrl}/users/admin/all-with-subscriptions-paginated?page=${page}&limit=${pageSize}`);
    }

    /**
     * Actualiza el tipo de usuario
     * @param userId ID del usuario
     * @param typeId ID del tipo de usuario
     * @returns Observable con la respuesta del servidor
     */
    updateUserType(userId: string, typeId: number): Observable<BaseResponse<User>> {
        return this.http.put<BaseResponse<User>>(`${environment.apiUrl}/users/${userId}/type`, { typeId });
    }

    /**
     * Actualiza la foto de perfil del usuario
     * @param userId ID del usuario
     * @param photo Foto en formato base64
     * @returns Observable con la respuesta del servidor
     */
    updateProfilePhoto(userId: string, photo: string): Observable<BaseResponse<User>> {
        // Añadir headers específicos si es necesario para archivos grandes
        const headers = new HttpHeaders({
            'Content-Type': 'application/json'
        });

        return this.http.post<BaseResponse<User>>(
            `${environment.apiUrl}/users/${userId}/profile-photo`,
            { photo },
            { headers }
        );
    }

    /**
     * Convierte un archivo a formato base64
     * @param file Archivo a convertir
     * @returns Promesa con el string en base64
     */
    fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    }

    // Actualiza la disponibilidad del coach
    updateCoachAvailability(param: any): Observable<BaseResponse<User>> {
        return this.http.post<BaseResponse<User>>(`${environment.apiUrl}/users/coach-availability`, param);
    }

    updateCoachType(userId: string, typeId: number): Observable<BaseResponse<User>> {
        return this.http.put<BaseResponse<User>>(`${environment.apiUrl}/users/${userId}/coach-type`, { typeId });
    }

    updateExperCategory(userId: string, categoryId: number): Observable<BaseResponse<User>> {
        return this.http.put<BaseResponse<User>>(`${environment.apiUrl}/users/${userId}/expert-category`, { categoryId });
    }

    updateExperCategories(userId: string, categoryIds: number[]): Observable<BaseResponse<User>> {
        return this.http.put<BaseResponse<User>>(`${environment.apiUrl}/users/${userId}/expert-categories`, { categoryIds });
    }

    createUser(user: any): Observable<BaseResponse<User>> {
        return this.http.post<BaseResponse<User>>(`${environment.apiUrl}/users/create`, user);
    }

    updateUser(userId: string, user: any): Observable<BaseResponse<User>> {
        return this.http.put<BaseResponse<User>>(`${environment.apiUrl}/users/${userId}/updateUsers`, user);
    }

    updateSubscriptionType(userId: string, subscriptionTypeId: number): Observable<BaseResponse<User>> {
        return this.http.patch<BaseResponse<User>>(`${environment.apiUrl}/subscriptions/${userId}/type`, { subscriptionTypeId });
    }

    updatePermissions(userId: string, permissions: any[]): Observable<BaseResponse<User>> {
        return this.http.patch<BaseResponse<User>>(`${environment.apiUrl}/users/${userId}/permissions`, permissions );
    }

    /**
     * Limpia/elimina una cita específica del coach/experto
     * @param coachId ID del coach/experto
     * @param appointmentData Datos de la cita a eliminar
     * @returns Observable con la respuesta del servidor
     */
    cleanCoachAppointment(coachId: string, appointmentData: { date: string; time: string; bookedByUserId: string }): Observable<BaseResponse<User>> {
        return this.http.post<BaseResponse<User>>(`${environment.apiUrl}/users/${coachId}/clean-appointment`, appointmentData);
    }

    /**
     * Método de prueba para verificar que el endpoint esté funcionando
     * @param coachId ID del coach/experto
     * @returns Observable con la respuesta del servidor
     */
    testCleanAppointmentEndpoint(coachId: string): Observable<any> {
        return this.http.get(`${environment.apiUrl}/users/${coachId}/test-clean-appointment`);
    }

    /**
     * Health check para verificar que el controlador de usuarios funciona
     * @returns Observable con la respuesta del servidor
     */
    healthCheck(): Observable<any> {
        return this.http.get(`${environment.apiUrl}/users/health-check`);
    }

    /**
     * Obtiene las respuestas de la guía de reflexión personal
     */
    getReflectionGuideAnswers(userId: string): Observable<BaseResponse<any>> {
        return this.http.get<BaseResponse<any>>(`${environment.apiUrl}/users/${userId}/reflection-guide`);
    }

    /**
     * Guarda las respuestas de la guía de reflexión personal
     * @param userId ID del usuario
     * @param reflectionData Datos de las respuestas de reflexión
     * @returns Observable con la respuesta del servidor
     */
    saveReflectionGuideAnswers(userId: string, reflectionData: {
        sections: any[];
        answers: any[];
        completedAt: string;
    }): Observable<BaseResponse<User>> {
        return this.http.post<BaseResponse<User>>(`${environment.apiUrl}/users/${userId}/reflection-guide`, reflectionData);
    }

    /**
     * Obtiene los datos del mapa de identidad
     * @param userId ID del usuario
     * @returns Observable con la respuesta del servidor
     */
    getIdentityMapData(userId: string): Observable<BaseResponse<any>> {
        return this.http.get<BaseResponse<any>>(`${environment.apiUrl}/users/${userId}/identity-map`);
    }

    /**
     * Guarda los datos del mapa de identidad
     * @param userId ID del usuario
     * @param identityData Datos del mapa de identidad
     * @returns Observable con la respuesta del servidor
     */
    saveIdentityMapData(userId: string, identityData: any): Observable<BaseResponse<User>> {
        return this.http.post<BaseResponse<User>>(`${environment.apiUrl}/users/${userId}/identity-map`, identityData);
    }

    /**
     * Guarda los datos específicos de Mi Presente
     * @param userId ID del usuario
     * @param miPresenteData Datos de Mi Presente
     * @returns Observable con la respuesta del servidor
     */
    saveMiPresenteData(userId: string, miPresenteData: {
        presentQuadrants: any[];
        stopDoing: string;
        doubleDown: string;
        objectives: string;
        completedAt: string;
    }): Observable<BaseResponse<User>> {
        return this.http.post<BaseResponse<User>>(`${environment.apiUrl}/users/${userId}/identity-map/mi-presente`, miPresenteData);
    }

    /**
     * Guarda los datos de priorización (step4)
     * @param userId ID del usuario
     * @param priorizationData Datos de priorización
     * @returns Observable con la respuesta del servidor
     */
    savePriorizationData(userId: string, priorizationData: {
        prioridades: any[];
        priorizacionCompleted: boolean;
        priorizacionEnabled: boolean;
    }): Observable<BaseResponse<any>> {
        return this.http.post<BaseResponse<any>>(`${environment.apiUrl}/users/${userId}/priorization`, priorizationData);
    }

    /**
     * Obtiene los datos de priorización (step4)
     * @param userId ID del usuario
     * @returns Observable con la respuesta del servidor
     */
    getPriorizationData(userId: string): Observable<BaseResponse<any>> {
        return this.http.get<BaseResponse<any>>(`${environment.apiUrl}/users/${userId}/priorization`);
    }

    /**
     * Actualiza el step5 (Phoenix Sessions)
     * @param userId ID del usuario
     * @param phoenixSessionField Campo de la sesión de Phoenix (ej: 'phoenixSessions7Completed')
     * @param completedAt Fecha de completado
     * @returns Observable con la respuesta del servidor
     */
    updateStep5(userId: string, phoenixSessionField: string, completedAt: string): Observable<BaseResponse<User>> {
        return this.http.post<BaseResponse<User>>(
            `${environment.apiUrl}/users/${userId}/step5`,
            {
                phoenixSessionField,
                completedAt
            }
        );
    }

}